import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, CheckCircle2, ClipboardList, Clock, FileCheck2, FileSignature, History, KeyRound, Landmark, Lock, Mail, MapPin, Menu, Scale, Search, ShieldCheck, Users, X } from "lucide-react";
import api from "../api";
import { useLang } from "../i18n";
import { LANDING, SOURCES_LIST, SOURCE_LOGOS, VERSION } from "../landing_i18n";
import { LEGAL } from "../legal_texts";

const NAV = [["platform", "#plataforma"], ["sources", "#fuentes"], ["compliance", "#cumplimiento"], ["security", "#seguridad"], ["contact", "#contacto"]];
const MOD_ICONS = [ShieldCheck, Users, ClipboardList, FileSignature, KeyRound, History];
const SEC_ICONS = [Lock, KeyRound, History, FileCheck2];
const STEP_ICONS = [Building2, ClipboardList, Search, FileCheck2];

function Reveal({ children, className = "", delay = 0 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div className={className} initial={reduce ? false : { opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function Kicker({ children }) {
  return <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C89B3C]">{children}</div>;
}

function PanamaClock({ label }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  const s = now.toLocaleString("es-PA", { timeZone: "America/Panama", weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return <div className="inline-flex items-center gap-2 text-sm text-white/80" data-testid="landing-panama-clock"><Clock className="w-4 h-4 text-[#C89B3C]" /> {label}: <span className="font-mono font-semibold text-white">{s}</span></div>;
}

function Header({ L, lang, setLang }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { const f = () => setScrolled(window.scrollY > 12); f(); window.addEventListener("scroll", f); return () => window.removeEventListener("scroll", f); }, []);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  const LangSwitch = ({ dark }) => (
    <div className={`inline-flex rounded-full border p-0.5 ${dark ? "border-white/20" : "border-slate-200"}`} data-testid="landing-lang-switch">
      {["es", "en"].map((l) => <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase min-w-[44px] ${lang === l ? "bg-[#0A192F] text-white" : dark ? "text-white/80" : "text-slate-600"}`} data-testid={`landing-lang-${l}`}>{l}</button>)}
    </div>
  );
  return (
    <>
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${open ? "bg-white border-b border-slate-200" : scrolled ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm" : "bg-transparent"}`} style={{ paddingTop: "env(safe-area-inset-top)" }} data-testid="landing-header">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center shrink-0" data-testid="landing-logo">
          <img src="/logo.png" alt="KYCFederal" width="150" height="40" className={`h-9 w-auto object-contain ${scrolled || open ? "" : "brightness-0 invert"}`} />
        </a>
        <nav className="hidden lg:flex items-center gap-7" aria-label="Principal">
          {NAV.map(([k, href]) => <a key={k} href={href} className={`text-sm font-semibold transition-colors ${scrolled ? "text-slate-700 hover:text-[#0A192F]" : "text-white/85 hover:text-white"}`} data-testid={`landing-nav-${k}`}>{L.nav[k]}</a>)}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <LangSwitch dark={!scrolled} />
          <Link to="/login" className={`px-4 py-2.5 rounded-full text-sm font-bold border transition-colors ${scrolled ? "border-slate-300 text-[#0A192F] hover:bg-slate-50" : "border-white/40 text-white hover:bg-white/10"}`} data-testid="landing-login-btn">{L.login}</Link>
          <Link to="/login?mode=register" className="px-4 py-2.5 rounded-full text-sm font-bold bg-[#C89B3C] text-[#0A192F] hover:bg-[#d8ab4c] transition-colors" data-testid="landing-register-btn">{L.register}</Link>
        </div>
        <button onClick={() => setOpen(!open)} className={`lg:hidden w-11 h-11 inline-flex items-center justify-center rounded-lg ${scrolled || open ? "text-[#0A192F]" : "text-white"}`} aria-label={L.menu} aria-expanded={open} data-testid="landing-menu-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
    </header>
      {open && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-white flex flex-col px-6 py-6 gap-2 overflow-y-auto" style={{ top: "calc(4rem + env(safe-area-inset-top))", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }} data-testid="landing-mobile-menu">
          {NAV.map(([k, href]) => <a key={k} href={href} onClick={() => setOpen(false)} className="py-3 text-lg font-semibold text-[#0A192F] border-b border-slate-100 min-h-[44px]" data-testid={`landing-mnav-${k}`}>{L.nav[k]}</a>)}
          <div className="pt-4"><LangSwitch /></div>
          <Link to="/login" className="mt-3 w-full text-center px-4 py-3.5 rounded-full text-base font-bold border border-slate-300 text-[#0A192F]" data-testid="landing-mlogin-btn">{L.login}</Link>
          <Link to="/login?mode=register" className="w-full text-center px-4 py-3.5 rounded-full text-base font-bold bg-[#0A192F] text-white" data-testid="landing-mregister-btn">{L.register}</Link>
        </div>
      )}
    </>
  );
}

function Hero({ L }) {
  return (
    <section id="top" className="relative bg-[#0A192F] text-white overflow-hidden" data-testid="landing-hero">
      <picture className="absolute inset-0">
        <source media="(max-width: 767px)" srcSet="/hero-corporate-sm.webp" />
        <img src="/hero-corporate.webp" alt="" className="w-full h-full object-cover opacity-45" fetchpriority="high" />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/70" />
      <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-semibold tracking-wide" data-testid="landing-ssnf-badge"><span className="font-extrabold">SSNF</span> · {L.ssnf}</div>
          <h1 className="font-heading font-extrabold leading-[1.1] tracking-tight" style={{ fontSize: "clamp(1.75rem, 4.5vw, 3.5rem)" }} data-testid="landing-hero-title">{L.hero_title}</h1>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed" data-testid="landing-hero-sub">{L.hero_sub}</p>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link to="/login?mode=register" className="w-full sm:w-auto text-center px-7 py-3.5 rounded-full font-bold bg-[#C89B3C] text-[#0A192F] hover:bg-[#d8ab4c] transition-colors min-h-[48px]" data-testid="landing-hero-register">{L.register}</Link>
            <Link to="/login" className="w-full sm:w-auto text-center px-7 py-3.5 rounded-full font-bold border border-white/40 text-white hover:bg-white/10 transition-colors min-h-[48px]" data-testid="landing-hero-login">{L.login}</Link>
          </div>
          <PanamaClock label={L.panama_time} />
        </div>
        <div className="lg:col-span-5 grid gap-3">
          {L.hero_points.map((p, i) => (
            <Reveal key={p} delay={0.1 * i}>
              <div className="flex items-start gap-3 rounded-xl border border-white/15 bg-white/5 backdrop-blur px-5 py-4" data-testid={`landing-hero-point-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-[#C89B3C] shrink-0 mt-0.5" /><span className="text-sm sm:text-base font-semibold">{p}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Section({ id, kicker, title, sub, children, alt }) {
  return (
    <section id={id} className={`py-16 sm:py-24 scroll-mt-16 ${alt ? "bg-[#F5F7FA]" : "bg-white"}`} data-testid={`landing-section-${id}`}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
        <Reveal className="max-w-2xl space-y-3 mb-10 sm:mb-14">
          <Kicker>{kicker}</Kicker>
          <h2 className="font-heading font-extrabold text-[#0A192F] tracking-tight" style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>{title}</h2>
          {sub && <p className="text-base text-slate-600 leading-relaxed">{sub}</p>}
        </Reveal>
        {children}
      </div>
    </section>
  );
}

function Card({ icon: Icon, title, text, i }) {
  return (
    <Reveal delay={0.06 * i}>
      <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 transition-transform duration-200 [@media(hover:hover)]:hover:-translate-y-1 [@media(hover:hover)]:hover:shadow-lg" data-testid={`landing-card-${i}`}>
        <div className="w-11 h-11 rounded-xl bg-[#0A192F] text-[#C89B3C] flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></div>
        <h3 className="font-heading font-bold text-[#0A192F] text-lg mb-2">{title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
      </div>
    </Reveal>
  );
}

function ContactForm({ L }) {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [st, setSt] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSt("sending");
    try { await api.post("/public/contact", f); setSt("ok"); setF({ name: "", company: "", email: "", phone: "", message: "" }); } catch { setSt("err"); }
  };
  const inp = "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-[#0A192F] focus:outline-none focus:ring-2 focus:ring-[#0A192F]/30 min-h-[48px]";
  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-4 shadow-sm" data-testid="landing-contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input className={inp} placeholder={L.f_name} value={f.name} onChange={set("name")} required autoComplete="name" data-testid="contact-name" />
        <input className={inp} placeholder={L.f_company} value={f.company} onChange={set("company")} autoComplete="organization" data-testid="contact-company" />
        <input className={inp} type="email" placeholder={L.f_email} value={f.email} onChange={set("email")} required autoComplete="email" data-testid="contact-email" />
        <input className={inp} type="tel" placeholder={L.f_phone} value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="contact-phone" />
      </div>
      <textarea className={`${inp} min-h-[130px]`} placeholder={L.f_message} value={f.message} onChange={set("message")} required data-testid="contact-message" />
      <button type="submit" disabled={st === "sending"} className="w-full sm:w-auto px-7 py-3.5 rounded-full font-bold bg-[#0A192F] text-white hover:bg-[#12274a] disabled:opacity-60 min-h-[48px]" data-testid="contact-submit">{st === "sending" ? L.f_sending : L.f_send}</button>
      {st === "ok" && <p className="text-sm font-semibold text-emerald-700" data-testid="contact-ok">{L.f_ok}</p>}
      {st === "err" && <p className="text-sm font-semibold text-red-700" data-testid="contact-err">{L.f_err}</p>}
    </form>
  );
}

function Footer({ L }) {
  return (
    <footer className="bg-[#0A192F] text-white/80" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="landing-footer">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-4">
          <img src="/logo.png" alt="KYCFederal" width="150" height="40" className="h-9 w-auto object-contain brightness-0 invert" loading="lazy" />
          <p className="text-sm leading-relaxed">{L.hero_points[0]}.</p>
          <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="landing-footer-seals">
            <img src="/iso27001-transparent.png" alt={L.iso_alt} className="h-10 w-auto object-contain bg-white rounded px-1.5 py-1" loading="lazy" />
            <img src="/wca-assoc.png" alt={L.wca_alt} className="h-10 w-auto object-contain bg-white rounded px-1.5 py-1" loading="lazy" />
            <img src="/latca-transparent.png" alt="LATCA — Latin American Compliance Association" className="h-10 w-auto object-contain bg-white rounded px-1.5 py-1" loading="lazy" data-testid="landing-footer-latca" />
            <img src="/asocupa-transparent.png" alt="ASOCUPA" className="h-10 w-auto object-contain bg-white rounded px-1.5 py-1" loading="lazy" data-testid="landing-footer-asocupa" />
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#C89B3C] mb-4">{L.footer_links}</div>
          <ul className="space-y-2.5 text-sm">{NAV.map(([k, href]) => <li key={k}><a href={href} className="hover:text-white inline-block py-1">{L.nav[k]}</a></li>)}</ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#C89B3C] mb-4">{L.footer_contact}</div>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-2"><Mail className="w-4 h-4 shrink-0 mt-0.5" /><a href="mailto:cumplimiento@kycfederal.com" className="hover:text-white break-all">cumplimiento@kycfederal.com</a></li>
            <li className="flex gap-2"><MapPin className="w-4 h-4 shrink-0 mt-0.5" /><span>{L.address}</span></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#C89B3C] mb-4">Legal</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/privacidad" className="hover:text-white inline-block py-1" data-testid="landing-privacy-link">{L.privacy}</Link></li>
            <li><Link to="/terminos" className="hover:text-white inline-block py-1" data-testid="landing-terms-link">{L.terms}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-6 space-y-3 text-xs text-white/60">
          <p data-testid="landing-disclaimer">{L.disclaimer}</p>
          <p data-testid="landing-copyright">© 2026 KYCFederal — {VERSION}</p>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const { lang, setLang } = useLang();
  const L = LANDING[lang] || LANDING.es;
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  return (
    <div className="min-h-screen bg-white text-[#0A192F] overflow-x-hidden scroll-smooth" data-testid="landing-page">
      <Header L={L} lang={lang} setLang={setLang} />
      <main>
        <Hero L={L} />
        <Section id="plataforma" kicker={L.platform_kicker} title={L.platform_title} sub={L.platform_sub}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{L.modules.map(([t, d], i) => <Card key={t} icon={MOD_ICONS[i]} title={t} text={d} i={i} />)}</div>
        </Section>
        <Section id="fuentes" kicker={L.sources_kicker} title={L.sources_title} sub={L.sources_sub} alt>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="landing-sources">
            {SOURCES_LIST.map((s, i) => (
              <Reveal key={s} delay={0.04 * i} className="min-w-0">
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-4 text-center font-heading font-bold text-sm sm:text-base text-[#0A192F] tracking-wide min-h-[112px] flex flex-col items-center justify-center gap-2.5 break-words" data-testid={`landing-source-${i}`}>
                  {SOURCE_LOGOS[s] ? (
                    <img src={SOURCE_LOGOS[s]} alt={`Logo ${s}`} loading="lazy" className="h-11 w-auto max-w-[120px] object-contain" data-testid={`landing-source-logo-${i}`} />
                  ) : (
                    <span className="h-11 w-11 rounded-full bg-[#0A192F] flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-[#C89B3C]" aria-hidden="true" /></span>
                  )}
                  <span className="leading-tight">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
        <Section id="cumplimiento" kicker={L.compliance_kicker} title={L.compliance_title} sub={L.compliance_sub}>
          <ol className="relative border-l-2 border-[#C89B3C]/60 ml-3 lg:ml-0 lg:border-l-0 lg:grid lg:grid-cols-4 lg:gap-6" data-testid="landing-timeline">
            {L.laws.map(([t, who, d], i) => (
              <Reveal key={t} delay={0.08 * i}>
                <li className="relative pl-8 pb-10 lg:pl-0 lg:pb-0 lg:pt-8 lg:border-t-2 lg:border-[#C89B3C]/60">
                  <span className="absolute -left-[11px] top-0 lg:left-0 lg:-top-[11px] w-5 h-5 rounded-full bg-[#0A192F] border-4 border-[#C89B3C]" />
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{who}</div>
                  <h3 className="font-heading font-bold text-[#0A192F] text-lg flex items-center gap-2"><Scale className="w-4 h-4 text-[#C89B3C]" /> {t}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </Section>
        <Section id="seguridad" kicker={L.security_kicker} title={L.security_title} alt>
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">{L.security_points.map(([t, d], i) => <Card key={t} icon={SEC_ICONS[i]} title={t} text={d} i={i} />)}</div>
            <Reveal className="lg:col-span-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-8 flex flex-col items-center gap-8" data-testid="landing-certifications">
                <img src="/iso27001-transparent.png" alt={L.iso_alt} className="h-24 w-auto max-w-full object-contain" loading="lazy" />
                <img src="/wca-assoc.png" alt={L.wca_alt} className="h-20 w-auto max-w-full object-contain" loading="lazy" />
                <img src="/latca-transparent.png" alt="LATCA — Latin American Compliance Association" className="h-16 w-auto max-w-full object-contain" loading="lazy" data-testid="landing-cert-latca" />
                <img src="/asocupa-transparent.png" alt="ASOCUPA" className="h-12 w-auto max-w-full object-contain" loading="lazy" data-testid="landing-cert-asocupa" />
              </div>
            </Reveal>
          </div>
        </Section>
        <Section id="como-funciona" kicker={L.how_kicker} title={L.how_title}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {L.steps.map(([t, d], i) => {
              const Icon = STEP_ICONS[i];
              return (
                <Reveal key={t} delay={0.08 * i}>
                  <div className="h-full rounded-2xl bg-[#0A192F] text-white p-6 sm:p-7 relative overflow-hidden" data-testid={`landing-step-${i}`}>
                    <span className="absolute right-4 top-3 font-heading text-6xl font-extrabold text-white/5">{i + 1}</span>
                    <div className="w-11 h-11 rounded-xl bg-white/10 text-[#C89B3C] flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></div>
                    <h3 className="font-heading font-bold text-lg mb-2">{i + 1}. {t}</h3>
                    <p className="text-sm text-white/75 leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Section>
        <section className="bg-[#0A192F] text-white py-16 sm:py-20" data-testid="landing-cta">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="flex-1 space-y-3">
              <h2 className="font-heading font-extrabold tracking-tight" style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>{L.cta_title}</h2>
              <p className="text-white/75 text-base">{L.cta_sub}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/login?mode=register" className="text-center px-7 py-3.5 rounded-full font-bold bg-[#C89B3C] text-[#0A192F] hover:bg-[#d8ab4c] min-h-[48px]" data-testid="landing-cta-register">{L.register}</Link>
              <a href="#contacto" className="text-center px-7 py-3.5 rounded-full font-bold border border-white/40 hover:bg-white/10 min-h-[48px]" data-testid="landing-cta-contact">{L.contact_team}</a>
            </div>
          </div>
        </section>
        <Section id="contacto" kicker={L.contact_kicker} title={L.contact_title}>
          <div className="grid lg:grid-cols-12 gap-8">
            <Reveal className="lg:col-span-4 space-y-6">
              <div className="flex gap-3"><Mail className="w-5 h-5 text-[#C89B3C] shrink-0 mt-1" /><div><div className="text-xs font-bold uppercase tracking-widest text-slate-500">{L.contact_email}</div><a href="mailto:cumplimiento@kycfederal.com" className="font-semibold text-[#0A192F] break-all" data-testid="landing-contact-email">cumplimiento@kycfederal.com</a></div></div>
              <div className="flex gap-3"><MapPin className="w-5 h-5 text-[#C89B3C] shrink-0 mt-1" /><div><div className="text-xs font-bold uppercase tracking-widest text-slate-500">{L.contact_address}</div><p className="font-semibold text-[#0A192F]" data-testid="landing-contact-address">{L.address}</p></div></div>
              <div className="flex gap-3"><Landmark className="w-5 h-5 text-[#C89B3C] shrink-0 mt-1" /><div><div className="text-xs font-bold uppercase tracking-widest text-slate-500">SSNF</div><p className="text-sm text-slate-600">{L.ssnf}</p></div></div>
            </Reveal>
            <div className="lg:col-span-8"><ContactForm L={L} /></div>
          </div>
        </Section>
      </main>
      <Footer L={L} />
    </div>
  );
}

export function LegalPage({ kind }) {
  const { lang, setLang } = useLang();
  const L = LANDING[lang] || LANDING.es;
  const T = LEGAL[lang] || LEGAL.es;
  const sections = kind === "privacy" ? T.privacy : T.terms;
  useEffect(() => { window.scrollTo(0, 0); }, [kind]);
  return (
    <div className="min-h-screen bg-[#F5F7FA] text-[#0A192F]" data-testid={`legal-${kind}`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A192F] hover:underline min-h-[44px]" data-testid="legal-back">← {L.legal_back}</Link>
          <div className="inline-flex rounded-full border border-slate-300 p-0.5">{["es", "en"].map((l) => <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase min-w-[44px] ${lang === l ? "bg-[#0A192F] text-white" : "text-slate-600"}`} data-testid={`legal-lang-${l}`}>{l}</button>)}</div>
        </div>
        <img src="/logo.png" alt="KYCFederal" className="h-10 w-auto object-contain" />
        <h1 className="font-heading font-extrabold tracking-tight" style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>{kind === "privacy" ? L.privacy : L.terms}</h1>
        <p className="text-sm text-slate-500" data-testid="legal-updated">{T.updated}</p>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-6" data-testid="legal-body">
          {sections.map(([h, p]) => (
            <section key={h}>
              <h2 className="font-heading font-bold text-[#0A192F] text-lg mb-2">{h}</h2>
              <p className="text-base text-slate-700 leading-relaxed whitespace-pre-line">{p}</p>
            </section>
          ))}
        </div>
        <p className="text-xs text-slate-500">© 2026 KYCFederal — {VERSION}</p>
      </div>
    </div>
  );
}
